import { useState, useEffect, useRef } from 'react'
import { dia, shapes } from '@joint/core'
import { ObjectType, useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'
import { useDefaultObjectSize } from '../../../map-editor-shared/store/editorStore'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useZoneDrawingStore } from '../../../map-editor-shared/store/zoneDrawingStore'
import { createSingleClickObject, finishLineObject, finishPolygonObject } from '../lib/objectCreationCore'

export function useObjectCreation(
    paper: dia.Paper | null,
    graph: dia.Graph | null,
    selectedType: ObjectType | null,
    onCreationComplete: () => void
) {
    const [isDrawing, setIsDrawing] = useState(false)
    const [points, setPoints] = useState<{ x: number; y: number }[]>([])
    const tempElementRef = useRef<dia.Element | null>(null)
    const defaultObjectSize = useDefaultObjectSize()
    const { currentFloor } = useFloorStore()
    const getLayerTypeMappingsByFloorId = useObjectTypeStore(state => state.getLayerTypeMappingsByFloorId)

    useEffect(() => {
        if (!paper || !graph || !selectedType) return

        const kind = selectedType.kind || 'asset'

        const handlePointerDown = (_evt: dia.Event, x: number, y: number) => {
            // Zone drawing takes exclusive control of pointer events.
            if (useZoneDrawingStore.getState().mode !== 'idle') return

            // Single-click kinds (label / icon / colored-rect) via shared core —
            // identical logic is reused by the Canvas overlay path.
            const mappings = getLayerTypeMappingsByFloorId(currentFloor || '')
            const created = createSingleClickObject(graph, selectedType, x, y, {
                mappings,
                defaultObjectSize,
            })
            if (created) {
                onCreationComplete()
                return
            }

            // Multi-click kinds: begin/continue interactive drawing.
            if (kind === 'line') {
                // Line drawing: 클릭으로 점 추가, 더블클릭으로 완성 (open polyline)
                if (!isDrawing) {
                    setIsDrawing(true)
                    setPoints([{ x, y }])

                    const path = new shapes.standard.Path()
                    const strokeColor = selectedType.strokeColor || selectedType.color || '#3b82f6'
                    path.attr({
                        body: {
                            fill: 'none',
                            stroke: strokeColor,
                            strokeWidth: selectedType.strokeWidth ?? 2
                        }
                    })
                    path.addTo(graph)
                    tempElementRef.current = path
                } else {
                    setPoints(prev => [...prev, { x, y }])
                }
                return
            }

            // Polygon Drawing (Click points) - for area/region types
            if (!isDrawing) {
                setIsDrawing(true)
                setPoints([{ x, y }])

                // Create temp path/polygon
                const path = new shapes.standard.Path()
                const color = selectedType.color || '#3b82f6'
                path.attr({
                    body: {
                        fill: color,
                        fillOpacity: 0.2,
                        stroke: color,
                        strokeWidth: 2
                    }
                })
                path.addTo(graph)
                tempElementRef.current = path
            } else {
                setPoints(prev => [...prev, { x, y }])
            }
        }

        const handlePointerMove = (_evt: dia.Event, x: number, y: number) => {
            if (!isDrawing || !tempElementRef.current || selectedType.icon) return

            // Update Polygon Preview (Line to cursor)
            // For simplicity, just redraw path with current points + cursor
            const currentPoints = [...points, { x, y }]
            // Construct SVG path data
            const d = 'M ' + currentPoints.map(p => `${p.x} ${p.y}`).join(' L ')
            tempElementRef.current.attr('body/d', d)
        }

        const handlePointerUp = () => {
            // Icon objects are created directly on click, no drag needed
            // This handler is only for polygon drawing (no icon)
            return
        }

        const handleDoubleClick = () => {
            // Zone drawing uses explicit "완료" button; skip dblclick here.
            if (useZoneDrawingStore.getState().mode !== 'idle') return
            if (!isDrawing || !tempElementRef.current || selectedType.icon) return

            const finalElement = tempElementRef.current
            if (!finalElement) return
            const dblMappings = getLayerTypeMappingsByFloorId(currentFloor || '')

            if (kind === 'line') {
                const line = finishLineObject(graph, selectedType, points, {
                    mappings: dblMappings,
                    defaultObjectSize,
                })
                finalElement.remove()
                tempElementRef.current = null
                setIsDrawing(false)
                setPoints([])
                if (line) onCreationComplete()
                return
            }

            // Finish Polygon (shared core — reused by the Canvas overlay path).
            finishPolygonObject(graph, selectedType, points, {
                mappings: dblMappings,
                defaultObjectSize,
            })
            finalElement.remove()
            tempElementRef.current = null
            setIsDrawing(false)
            setPoints([])
            onCreationComplete()
        }

        paper.on('blank:pointerdown', handlePointerDown)
        paper.on('blank:pointermove', handlePointerMove)
        paper.on('blank:pointerup', handlePointerUp)
        paper.on('blank:pointerdblclick', handleDoubleClick)

        return () => {
            paper.off('blank:pointerdown', handlePointerDown)
            paper.off('blank:pointermove', handlePointerMove)
            paper.off('blank:pointerup', handlePointerUp)
            paper.off('blank:pointerdblclick', handleDoubleClick)
        }
    }, [paper, graph, selectedType, isDrawing, points, defaultObjectSize, currentFloor, getLayerTypeMappingsByFloorId])

    return { isDrawing }
}
